import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { useAppBranding } from '../../config/branding';
import CancelReservation from '../../ocpp-message-pages/cancel-reservation/CancelReservation';
import ChangeConfiguration from '../../ocpp-message-pages/change-configuration/ChangeConfiguration';
import ClearCache from '../../ocpp-message-pages/clear-cache/ClearCache';
import GetConfiguration from '../../ocpp-message-pages/get-configuration/GetConfiguration';
import GetDiagnostics from '../../ocpp-message-pages/get-diagnostics/GetDiagnostics';
import GetLocalVersionList from '../../ocpp-message-pages/get-local-list-version/GetLocalListVersion';
import OcppConsole from '../../ocpp-message-pages/ocpp-console/OcppConsole';
import RemoteStartTransaction from '../../ocpp-message-pages/remote-start-charging/RemoteStartTransaction';
import RemoteStopTransaction from '../../ocpp-message-pages/remote-stop-charging/RemoteStopTransaction';
import ReserveNow from '../../ocpp-message-pages/reserve-now/ReserveNow';
import Reset from '../../ocpp-message-pages/reset/Reset';
import TriggerMessage from '../../ocpp-message-pages/trigger-message/TriggerMessage';
import UnlockConnector from '../../ocpp-message-pages/unlock-connector/UnlockConnector';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setNotificationMessage } from '../../redux/slices/NotificationManagementSlice';
import Footer from '../../shared-components/footer/Footer';
import Header from '../../shared-components/header/Header';
import GlobalTableModal from '../../shared-components/modal-box/GlobalTableModal';
import OCPPMessageSidebar from '../../shared-components/ocpp-message/sidebar/Sidebar';
import Sidebar from '../../shared-components/sidebar/Sidebar';
import ToastMessage from '../../shared-components/toast-message/ToastMessage';
import AccountManagement from '../account-management';
import AccountsManagementPayout from '../account-management/accounts-management-payout';
import UtilityProvider from '../account-management/utilityProvider';
import CampaignManagement from '../campaign';
import CreateCampaign from '../campaign/components/CreateCampaign';
import ViewCampaign from '../campaign/components/ViewCampaign';
import ChargingSessions from '../charging-session';
import ChargingStationManagement from '../charging-station';
import Dashboard from '../dashboard';
import CreateNewDashboard from '../dashboard/components/CreateNewDashboard';
import CustomizeDashboard from '../dashboard/components/CustomizeDashboard';
// import DeviceQrManagement from '../device-qr-management';
import DeviceListing from '../device-management';
import DeviceManagement from '../device-management/DeviceManagement';
import Websocket from '../device-management/Websocket';
import QRCodeInputFields from '../device-qr-customization/components/QrCodeInputField';
// import DeviceQrManagement from '../device-qr-management';
import DynamicPricing from '../dynamic-pricing';
// import EndConsumerManagement from '../end-consumer-management';
import EndConsumerManagementV2 from '../end-consumer-management-v2';
import EndConsumersProfile from '../end-consumer-management-v2/components/EndConsumerProfile';
import FeeConfiguration from '../fee-configuration';
// import Login from '../login';
// import ForgetPassword from '../login/components/forgot-password';
// import NewPasswordChange from '../login/components/new-password-change';
// import OTPVerification from '../login/components/otp-verification';
import LoginV2 from '../login-v2/components/Login';
import AuthLayout from '../login-v2/index';
import PaymentManagement from '../payments';
import PenaltyManagement from '../penalty-management';
import ReportManagement from '../reports';
import RevenueManagement from '../revenue-management';
import StationManagementSidePanel from '../station-management-side-panel';
import StripePayouts from '../stripe-payouts';
import UserManagement from '../user-management';

import AuthGuard from './components/AuthGuard';
/**
 * Wrapper Route function component
 */
function WrapperRoute() {
  const { i18n } = useTranslation();
  const { meta: brandMeta } = useAppBranding();
  const location = useLocation();
  const [showSidebar, setShowSidebar] = useState(true);
  const { open } = useAppSelector((state) => state.tableModal);
  const hideHeader = location.pathname.includes('dashboard/customize-dashboard');
  const { userCustomId } = useParams<{ userCustomId: string }>();
  const { type } = useParams<{ type: string }>();

  useEffect(() => {
    if (
      location.pathname.includes(`end-consumer/${type}/profile/${userCustomId}/station`) ||
      location.pathname.includes(`end-consumer/${type}/profile/${userCustomId}/device-expanded`) ||
      location.pathname.includes('device-management/device-expanded') ||
      location.pathname.includes('charging-station/approved/station-expanded') ||
      location.pathname.includes('charging-station/unapproved/station-expanded') ||
      location.pathname.includes('charging-station/rejected/station-expanded') ||
      location.pathname.includes('device-management/station-expanded') ||
      location.pathname.includes('transaction-management/station')
    ) {
      setShowSidebar(false);
    } else {
      setShowSidebar(true);
    }
  }, [location.pathname]);

  return showSidebar ? (
    <div className={`ev-layout-wrap ${i18n.language === 'ar' ? 'rtl' : ''}`}>
      <div className="ev-sidebar">
        <Sidebar />
      </div>
      <div className="ev-content-wrap" style={{ display: !hideHeader ? 'grid' : 'block' }}>
        {!hideHeader && <Header />}
        <div className="ev-content">
          <Outlet />
          {open && <GlobalTableModal />}
        </div>
        {brandMeta.footerText && <Footer />}
      </div>
    </div>
  ) : (
    <div className={`ev-layout-wrap hide-sidebar ${i18n.language === 'ar' ? 'rtl' : ''}`}>
      <div className="ev-content-wrap" style={{ display: !hideHeader ? 'grid' : 'block' }}>
        {!hideHeader && <Header />}
        <div className="ev-content">
          <Outlet />
        </div>
        {brandMeta.footerText && <Footer />}
      </div>
    </div>
  );
}

/**
 * Function to handle OCPP message commands
 * @returns
 */
function OcppWrapperRoute() {
  return (
    <div className="ev-layout-wrap">
      <div className="ev-sidebar">
        <OCPPMessageSidebar />
      </div>
      <div className="ocpp-message-content-wrap">
        <div className="ocpp-message-conetnt">
          <Outlet />
        </div>
        <div className="ocpp-console">
          <OcppConsole />
        </div>
      </div>
    </div>
  );
}

/**
 * Router page function component
 */
function Router() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { notificationMessage, notificationType, notificationStatus, notificationCode } =
    useAppSelector((state) => state.notification);
  const routes = [
    {
      path: 'dashboard',
      children: [
        {
          id: 1,
          path: '',
          element: <Dashboard />,
          permission: ['dashboard'],
        },
        {
          id: 2,
          path: 'customize-dashboard',
          element: <CustomizeDashboard />,
          permission: ['dashboard'],
        },
        // {
        //   id: 3,
        //   path: 'create-new',
        //   element: <CreateNewDashboard />,
        //   permission: ['dashboard'],
        // },
      ],
    },
    {
      path: 'account',
      children: [
        {
          id: 1,
          path: '',
          element: <AccountManagement />,
          permission: ['accounts_management'],
        },
        {
          id: 2,
          path: 'payout-management',
          element: <AccountsManagementPayout />,
          permission: [],
        },
      ],
    },
    {
      path: 'utility-provider',
      children: [
        {
          id: 1,
          path: '',
          element: <UtilityProvider />,
          permission: ['accounts_management'],
        },
        {
          id: 2,
          path: 'payout-management',
          element: <AccountsManagementPayout />,
          permission: [],
        },
      ],
    },
    {
      path: 'charging-station',
      children: [
        {
          id: 1,
          path: ':currentPage',
          element: <ChargingStationManagement />,
          permission: ['charging_station'],
        },
        {
          id: 2,
          path: ':currentPage/station/:id/:tab',
          element: <StationManagementSidePanel />,
          permission: [],
        },
        {
          id: 2,
          path: ':currentPage/station-expanded/:id/:tab',
          element: <StationManagementSidePanel />,
          permission: [],
        },
      ],
    },
    {
      path: 'reports',
      children: [
        {
          id: 4,
          path: ':page',
          element: <ReportManagement />,
          permission: ['reporting_tools'],
        },
      ],
    },
    {
      path: 'user-management',
      children: [
        {
          id: 5,
          path: ':currentPage',
          element: <UserManagement />,
          permission: ['user_management'],
        },
      ],
    },
    // {
    //   path: 'end-consumer',
    //   children: [
    //     {
    //       id: 6,
    //       path: '',
    //       element: <EndConsumerManagement />,
    //       permission: ['end_consumer_management'],
    //     },
    //   ],
    // },
    {
      path: 'end-consumer',
      children: [
        {
          id: 6,
          path: '',
          element: <EndConsumerManagementV2 />,
          permission: ['end_consumer_management'],
        },
        {
          id: 6,
          path: ':type/profile/:userCustomId',
          element: <EndConsumersProfile />,
          permission: [],
        },
        {
          id: 6,
          path: ':type/profile/:userCustomId/station/:id/:tab',
          element: <StationManagementSidePanel />,
          permission: [],
        },
        {
          id: 6,
          path: ':type/profile/:userCustomId/device/:id/:tab',
          element: <DeviceManagement />,
          permission: [],
        },
        {
          id: 7,
          path: ':type/profile/:userCustomId/device-expanded/:id/:tab',
          element: <DeviceManagement />,
          permission: [],
        },
      ],
    },
    {
      path: 'payments',
      children: [
        {
          id: 7,
          path: '',
          element: <PaymentManagement />,
          permission: ['payments'],
        },
      ],
    },
    {
      path: 'charging-sessions',
      children: [
        {
          id: 11,
          path: '',
          element: <ChargingSessions />,
          permission: ['payments'],
        },
      ],
    },
    {
      path: 'revenue/:page',
      children: [
        {
          id: 8,
          path: '',
          element: <RevenueManagement />,
          permission: ['wallet_revenue_sharing'],
        },
      ],
    },
    {
      path: 'dynamic-pricing/:currentPage',
      children: [
        {
          id: 9,
          path: '',
          element: <DynamicPricing />,
          permission: ['dynamic_pricing'],
        },
      ],
    },
    {
      path: 'device-qr-management',
      children: [
        {
          id: 10,
          path: '',
          element: <QRCodeInputFields />,
          permission: ['qr_code_management'],
        },
      ],
    },
    {
      path: 'device-management',
      children: [
        {
          id: 1,
          path: '',
          element: <DeviceListing />,
          permission: ['device_management', 'view_device'],
        },
        {
          id: 2,
          path: 'device/:id/:tab',
          element: <DeviceManagement />,
          permission: ['device_management'],
        },
        {
          id: 3,
          path: 'device-expanded/:id/:tab',
          element: <DeviceManagement />,
          permission: ['device_management'],
        },
        {
          id: 4,
          path: 'websocket-management/:currentPage',
          element: <Websocket />,
          permission: ['device_web_socket_management'],
        },
        {
          id: 5,
          path: 'station/:id/:tab',
          element: <StationManagementSidePanel />,
          permission: [],
        },
        {
          id: 6,
          path: 'station-expanded/:id/:tab',
          element: <StationManagementSidePanel />,
          permission: [],
        },
      ],
    },
    {
      path: 'campaign-management',
      children: [
        {
          id: 1,
          path: '',
          element: <CampaignManagement />,
          permission: ['campaign_management'],
        },
        {
          id: 2,
          path: 'create',
          element: <CreateCampaign />,
          permission: ['campaign_management'],
        },
        {
          id: 3,
          path: 'view/:campaignId',
          element: <ViewCampaign />,
          permission: ['campaign_management'],
        },
      ],
    },
    {
      path: 'stripe-payouts/:currentPage',
      children: [
        {
          id: 1,
          path: '',
          element: <StripePayouts />,
          permission: ['stripe_payout_management'],
        },
      ],
    },
    {
      path: 'fee-configuration',
      children: [
        {
          id: 1,
          path: '',
          element: <FeeConfiguration />,
          permission: [],
        },
      ],
    },
    {
      path: 'penalty-management/:currentPage',
      children: [
        {
          id: 1,
          path: '',
          element: <PenaltyManagement />,
          permission: [],
        },
      ],
    },
  ];

  useEffect(() => {
    if (notificationCode === 401) {
      navigate('/login');
    }
  }, [notificationCode]);
  return (
    <>
      <Routes>
        {/* <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgetPassword />} />
        <Route path="/otp-verification" element={<OTPVerification />} />
        <Route path="/new-password" element={<NewPasswordChange />} /> */}
        <Route path="/" element={<Navigate to="/login" />} />
        <Route path="/ev-admin/create-custom-dashboard" element={<CreateNewDashboard />} />
        <Route path="/ev-admin/edit-custom-dashboard/:id" element={<CreateNewDashboard />} />
        <Route element={<AuthLayout />}>
          <Route path="login" element={<Navigate to="/login/credentials" />} />
          <Route path="login/:page" element={<LoginV2 />} />
        </Route>
        <Route path="/ev-admin" element={<WrapperRoute />}>
          {routes.map((route) => (
            <Route path={route.path}>
              {route.children.map((subRoute) => (
                <Route
                  key={subRoute?.id}
                  path={subRoute?.path}
                  element={
                    <AuthGuard permission={subRoute?.permission} Component={subRoute?.element} />
                  }
                />
              ))}
            </Route>
          ))}
        </Route>
        {/* Route to manage ocpp messages */}
        <Route path="/ocpp-message" element={<OcppWrapperRoute />}>
          <Route path="change-configuration" element={<ChangeConfiguration />} />
          <Route path="get-configuration" element={<GetConfiguration />} />
          <Route path="clear-cache" element={<ClearCache />} />
          <Route path="get-diagnostics" element={<GetDiagnostics />} />
          <Route path="remote-start-transaction" element={<RemoteStartTransaction />} />
          <Route path="remote-stop-transaction" element={<RemoteStopTransaction />} />
          <Route path="unlock-connector" element={<UnlockConnector />} />
          <Route path="reserve-now" element={<ReserveNow />} />
          <Route path="cancel-reservation" element={<CancelReservation />} />
          <Route path="reset" element={<Reset />} />
          <Route path="get-local-list-version" element={<GetLocalVersionList />} />
          <Route path="trigger-message" element={<TriggerMessage />} />
        </Route>
      </Routes>
      <ToastMessage
        open={notificationStatus}
        type={notificationType}
        message={notificationMessage || 'Something went wrong!'}
        onClose={() =>
          dispatch(
            setNotificationMessage({
              status: false,
              message: '',
              type: 'error',
              code: 0,
            }),
          )
        }
        onCloseTimer={3000}
      />
    </>
  );
}

export default Router;
